import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PayrollCompanyBrokerNotificationResult,
  TeamCompaniesService,
} from '../../../services/teamcompanies/teamcompanies.service';
import { UsersService } from '../../../services/users/users.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import {
  PayrollCompanyDialogComponent,
  PayrollCompanyDialogResult,
} from '../payroll-company-dialog/payroll-company-dialog.component';
import {
  PayrollBroker,
  PayrollBrokerDialogComponent,
  PayrollBrokerDialogResult,
} from '../payroll-broker-dialog/payroll-broker-dialog.component';
import {
  normalizePayrollBrokerAssignments,
} from '../payroll-broker-conflicts';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { Subscription } from 'rxjs';
import {
  PayrollProviderConnectionResponse,
  PayrollService,
} from '../../../services/payroll/payroll.service';

const DUPLICATE_DATA_HELP_MESSAGE = 'This information has already been used. Please contact help@wallit.app to connect your account to the existing company.';
const COMPANY_PROVISIONING_REQUIRED_MESSAGE = 'A Wallit administrator must provision the company and assign your Payroll Admin access first.';

@Component({
    selector: 'app-payroll-company',
    templateUrl: './payroll-company.component.html',
    styleUrls: ['./payroll-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollCompanyComponent implements OnInit, OnDestroy {
  private readonly allTabs = ['company', 'connections', 'broker'];
  private readonly brokerCategories = ['Health', 'Dental', 'Vision', 'Supplemental', 'HSA', 'FSA', 'Retirement', 'P&C'];
  private requestedTab = 'company';

  protected activeTab = 'company';
  protected selectedTabIndex = 0;
  protected visibleTabs = [...this.allTabs];

  protected isLoading = true;
  protected hasCompany = false;
  protected company?: any;
  protected brokers: PayrollBroker[] = [];
  protected payrollProviderConnection: PayrollProviderConnectionResponse | null = null;

  private meSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private teamCompaniesService: TeamCompaniesService,
    private usersService: UsersService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    private payrollService: PayrollService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.requestedTab = (params.get('tab') || 'company').toLowerCase();
      this.syncTabs();
    });

    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me) {
        return;
      }

      await this.loadCompanyState();
      this.syncTabs();
    });
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
  }

  protected onTabChange(index: number): void {
    const tab = this.visibleTabs[index] || 'company';
    if (tab !== this.activeTab) {
      this.router.navigate(['payrolladmin', 'company', tab]);
    }
  }

  protected onGoToCompanyTab(): void {
    void this.router.navigate(['payrolladmin', 'company', 'company']);
  }

  protected openCreateDialog(): void {
    this.snackBar.openFromComponent(SnackBarComponent, {
      data: COMPANY_PROVISIONING_REQUIRED_MESSAGE,
    });
  }

  protected openEditDialog(): void {
    if (!this.company?.id) {
      return;
    }

    this.openCompanyDialog(this.company);
  }

  protected confirmRemoveCompany(): void {
    if (!this.company?.id) {
      return;
    }

    const companyName = this.company?.name || 'this company';
    const message = [
      `If you want to remove company ${companyName}, email <a href="mailto:help@wallit.app">help@wallit.app</a>.`,
      `We want to make sure you don't lose any important data, so drop us an email and we'll take care of this for you.`,
    ].join('<br><br>');

    this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Company Deletion',
        message,
        true,
        'Got it',
      ),
      autoFocus: false,
    });
  }

  protected get dba(): string {
    return this.getPayrollCompanyInfo(this.company)?.dba || '--';
  }

  protected get contactName(): string {
    return this.getPayrollCompanyInfo(this.company)?.contact?.name || '--';
  }

  protected get contactEmail(): string {
    return this.getPayrollCompanyInfo(this.company)?.contact?.email || '--';
  }

  protected get hasBrokers(): boolean {
    return this.brokers.length > 0;
  }

  protected get needsPayrollConnection(): boolean {
    if (!this.hasCompany || !this.company) {
      return false;
    }

    return !this.hasProviderConnection(this.payrollProviderConnection);
  }

  protected get unassignedBrokerCategories(): string[] {
    const assigned = new Set<string>();
    this.brokers.forEach(broker => {
      (broker.categories || []).forEach(category => assigned.add(category));
    });
    return this.brokerCategories.filter(category => !assigned.has(category));
  }

  protected openAddBrokerDialog(): void {
    this.openBrokerDialog();
  }

  protected openEditBrokerDialog(broker: PayrollBroker): void {
    this.openBrokerDialog(broker);
  }

  protected async removeBroker(broker: PayrollBroker): Promise<void> {
    if (!this.company?.id) {
      return;
    }

    const ok = await this.uiAlertService.presentAlertConfirm(`Do you really want to remove broker ${broker.companyName}?`);
    if (!ok) {
      return;
    }

    const nextBrokers = this.brokers.filter(item => item.id !== broker.id);
    await this.saveBrokers(nextBrokers);
  }

  protected trackBroker(index: number, broker: PayrollBroker): string {
    return broker.id || `${broker.companyName}-${index}`;
  }

  private syncTabs(): void {
    this.visibleTabs = [...this.allTabs];

    const nextTab = this.visibleTabs.includes(this.requestedTab) ? this.requestedTab : 'company';
    this.activeTab = nextTab;
    this.selectedTabIndex = this.visibleTabs.indexOf(nextTab);

    if (!this.isLoading && nextTab !== this.requestedTab) {
      void this.router.navigate(['payrolladmin', 'company', nextTab], { replaceUrl: true });
    }
  }

  private async loadCompanyState(forceRefresh = false): Promise<void> {
    this.isLoading = true;
    try {
      const userCompanies = await this.usersService.getTeamCompanies(forceRefresh);
      this.hasCompany = (userCompanies?.length || 0) > 0;
      this.company = this.hasCompany
        ? await this.teamCompaniesService.getCompany(userCompanies[0].id).catch(() => userCompanies[0])
        : undefined;
      this.payrollProviderConnection = this.company?.id
        ? await this.payrollService.getCompanyPayrollProviderConnection(this.company.id).catch(() => null)
        : null;
      this.brokers = this.getRawBrokers(this.company);
    } finally {
      this.isLoading = false;
    }
  }

  private openCompanyDialog(company: any): void {
    const ref = this.dialog.open<PayrollCompanyDialogComponent, { company?: any }, PayrollCompanyDialogResult>(
      PayrollCompanyDialogComponent,
      {
        width: '760px',
        maxWidth: '95vw',
        data: { company },
        autoFocus: false,
      }
    );

    ref.afterClosed().subscribe(async result => {
      if (!result) {
        return;
      }

      try {
        await this.updateCompany(company.id, result);

        this.usersService.reloadMe();
        await this.loadCompanyState(true);
        this.syncTabs();
      } catch (error) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: this.getSaveErrorMessage(error),
        });
      }
    });
  }

  private async updateCompany(companyId: string, result: PayrollCompanyDialogResult): Promise<void> {
    const nextMetadata = {
      ...(this.company?.metadata || {}),
      ...(result.metadata || {}),
    };
    const updatedCompany = await this.teamCompaniesService.modifyCompanyPayrollConfig(
      companyId,
      (latestPayrollConfig) => this.withPayrollCompany(
        latestPayrollConfig,
        result?.metadata?.payrollCompany,
      ),
    );

    await this.teamCompaniesService.modifyCompany(companyId, {
      name: result.name,
      EIN: result.EIN,
      SIC: result.SIC,
      address1: result.address1,
      address2: result.address2,
      city: result.city,
      state: result.state,
      postcode: result.postcode,
      metadata: nextMetadata,
    });

    this.company = {
      ...(this.company || {}),
      ...(updatedCompany || {}),
      metadata: nextMetadata,
    };

    await this.persistLogo(companyId, result.imageData);
  }

  private async persistLogo(companyId: string, imageData: ArrayBuffer | null): Promise<void> {
    if (!imageData) {
      return;
    }

    const uploaded = await this.teamCompaniesService.uploadLogo(companyId, imageData);
    if (uploaded?.url) {
      await this.teamCompaniesService.modifyCompany(companyId, { logoUrl: uploaded.url });
    }
  }

  private openBrokerDialog(existingBroker?: PayrollBroker): void {
    if (!this.company?.id) {
      return;
    }

    const ref = this.dialog.open<
      PayrollBrokerDialogComponent,
      { broker?: PayrollBroker; categories: string[]; existingBrokers: PayrollBroker[] },
      PayrollBrokerDialogResult
    >(
      PayrollBrokerDialogComponent,
      {
        width: '760px',
        maxWidth: '95vw',
        data: {
          broker: existingBroker,
          categories: this.brokerCategories,
          existingBrokers: this.brokers,
        },
        autoFocus: false,
      }
    );

    ref.afterClosed().subscribe(async result => {
      if (!result) {
        return;
      }

      try {
        const list = normalizePayrollBrokerAssignments(this.brokers, result.broker, result.decisions);
        await this.saveBrokers(list);
      } catch (error) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: this.getBrokerSaveErrorMessage(error),
        });
      }
    });
  }

  private async saveBrokers(brokers: PayrollBroker[]): Promise<void> {
    if (!this.company?.id) {
      return;
    }

    const previousBrokers = this.getRawBrokers(this.company);
    const updatedCompany = await this.teamCompaniesService.modifyCompanyPayrollConfig(
      this.company.id,
      (latestPayrollConfig) => ({
        ...latestPayrollConfig,
        brokers,
      }),
    );

    this.company = {
      ...(this.company || {}),
      ...(updatedCompany || {}),
    };
    this.brokers = brokers;
    this.usersService.reloadMe();

    const warning = await this.notifyBrokerChangesIfNeeded(previousBrokers, brokers);
    if (warning) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: warning,
      });
    }
  }

  private getRawBrokers(company: any): PayrollBroker[] {
    const payrollConfig = this.getPayrollConfig(company);
    const items = Array.isArray(payrollConfig.brokers) ? payrollConfig.brokers : [];

    return items.map((item: any, index: number) => ({
      id: this.getBrokerId(item, index),
      companyName: this.getBrokerTextValue(item?.companyName),
      contactName: this.getBrokerTextValue(item?.contactName),
      email: this.getBrokerTextValue(item?.email),
      phone: this.getBrokerTextValue(item?.phone),
      categories: Array.isArray(item?.categories) ? item.categories.filter(Boolean) : [],
    }));
  }

  private hasProviderConnection(connection: PayrollProviderConnectionResponse | null | undefined): boolean {
    return this.payrollService.hasActivePayrollProviderConnection(connection);
  }

  private getPayrollCompanyInfo(company: any): any {
    return company?.metadata?.payrollCompany || this.getPayrollConfig(company)?.payrollCompany || {};
  }

  private withPayrollCompany(currentPayrollConfig: any, payrollCompanyData: any): any {
    const nextPayrollConfig = { ...this.getPayrollConfig({ payrollConfig: currentPayrollConfig }) };
    const nextPayrollCompany = {
      ...(nextPayrollConfig?.payrollCompany || {}),
      ...(payrollCompanyData || {}),
    };
    if (payrollCompanyData && Object.prototype.hasOwnProperty.call(payrollCompanyData, 'theme') && !payrollCompanyData.theme) {
      delete nextPayrollCompany.theme;
    }
    nextPayrollConfig.payrollCompany = nextPayrollCompany;
    return nextPayrollConfig;
  }

  private getPayrollConfig(company: any): any {
    const rawPayrollConfig = company?.payrollConfig;
    if (!rawPayrollConfig) {
      return {};
    }

    if (typeof rawPayrollConfig === 'string') {
      try {
        return JSON.parse(rawPayrollConfig);
      } catch (_) {
        return {};
      }
    }

    return typeof rawPayrollConfig === 'object' ? rawPayrollConfig : {};
  }

  private getBrokerId(item: any, index: number): string {
    return this.getBrokerTextValue(item?.id) || `legacy-broker-${index}`;
  }

  private getBrokerTextValue(value: any): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private async notifyBrokerChangesIfNeeded(
    beforeBrokers: PayrollBroker[],
    afterBrokers: PayrollBroker[],
  ): Promise<string> {
    if (!this.company?.id || !this.hasEffectiveBrokerDiff(beforeBrokers, afterBrokers)) {
      return '';
    }

    try {
      const result = await this.teamCompaniesService.sendBrokerOnboardingNotification(
        this.company.id,
        beforeBrokers,
        afterBrokers,
      );
      return this.getBrokerNotificationWarning(result);
    } catch (_) {
      return 'Broker changes were saved, but the onboarding notification email could not be sent.';
    }
  }

  private getBrokerNotificationWarning(result?: PayrollCompanyBrokerNotificationResult | null): string {
    return `${result?.warning || ''}`.trim();
  }

  private hasEffectiveBrokerDiff(beforeBrokers: PayrollBroker[], afterBrokers: PayrollBroker[]): boolean {
    return this.toComparableBrokerSnapshot(beforeBrokers) !== this.toComparableBrokerSnapshot(afterBrokers);
  }

  private toComparableBrokerSnapshot(brokers: PayrollBroker[]): string {
    const normalized = (Array.isArray(brokers) ? brokers : []).map((broker, index) => ({
      categories: Array.isArray(broker?.categories)
        ? [...broker.categories].filter(Boolean).sort((left, right) => left.localeCompare(right))
        : [],
      companyName: this.getBrokerTextValue(broker?.companyName),
      contactName: this.getBrokerTextValue(broker?.contactName),
      email: this.getBrokerTextValue(broker?.email),
      id: this.getBrokerTextValue(broker?.id) || `broker-${index}`,
      phone: this.getBrokerTextValue(broker?.phone),
    }));

    normalized.sort((left, right) => {
      const idComparison = left.id.localeCompare(right.id);
      if (idComparison !== 0) {
        return idComparison;
      }

      const nameComparison = left.companyName.localeCompare(right.companyName);
      if (nameComparison !== 0) {
        return nameComparison;
      }

      return left.email.localeCompare(right.email);
    });

    return JSON.stringify(normalized);
  }

  private getSaveErrorMessage(error: any): string {
    const errorCode = error?.error?.code || '';
    if (errorCode === 'DUPLICATE_COMPANY' || errorCode === 'DUPLICATE_UKG_CONNECTION') {
      return DUPLICATE_DATA_HELP_MESSAGE;
    }

    const backendMessage = `${error?.error?.error || ''}`.trim();
    if (backendMessage.includes('help@wallit.app')) {
      return backendMessage;
    }

    return 'Unable to save company information. Please try again.';
  }

  private getBrokerSaveErrorMessage(error: any): string {
    const backendMessage = `${error?.error?.error || ''}`.trim();
    return backendMessage || 'Unable to save broker changes. Please try again.';
  }
}
